import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book, Hash, Search, User, Mail, Lock, ArrowRight, Check, X } from 'lucide-react';
import AuthService from './AuthService';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { useToast } from '../hooks/useToast';

const FamilySelectionModal = ({ onClose, onComplete }) => {
  const [mode, setMode] = useState('code');
  const [familyCode, setFamilyCode] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFamilies, setSelectedFamilies] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async () => {
    if (!searchQuery) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await AuthService.searchFamilies(searchQuery);
      setSearchResults(data.families || []);
    } catch (err) {
      setError('Failed to search families');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const toggleFamilySelection = (family) => {
    setSelectedFamilies(prev => {
      const isSelected = prev.some(f => f.id === family.id);
      if (isSelected) {
        return prev.filter(f => f.id !== family.id);
      } else {
        return [...prev, family];
      }
    });
  };

  const handleCodeSubmit = async () => {
    if (!familyCode) return;
    
    setLoading(true);
    setError('');
    try {
      const data = await AuthService.joinFamily(familyCode);
      onComplete([data.family]);
    } catch (err) {
      setError(err.message || 'Failed to join family');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchComplete = async () => {
    if (selectedFamilies.length === 0) return;
    
    setLoading(true);
    setError('');
    try {
      const joinedFamilies = [];
      for (const family of selectedFamilies) {
        const data = await AuthService.joinFamily(family.code);
        joinedFamilies.push(data.family);
      }
      onComplete(joinedFamilies);
    } catch (err) {
      setError(err.message || 'Failed to join families');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete([]);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      zIndex: 1000
    }}>
      <Card style={{
        width: '100%',
        maxWidth: '32rem',
        maxHeight: '90vh',
        overflow: 'auto',
        padding: '2rem',
        position: 'relative'
      }}>
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '0.25rem'
          }}
        >
          <X style={{ width: '1.5rem', height: '1.5rem' }} />
        </button>

        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '1rem' }}>
          Join Your Family
        </h2>
        <p style={{ color: '#6b7280', marginBottom: '1.5rem' }}>
          Connect with your family by entering a code or searching for them, or skip for now
        </p>

        {error && (
          <div style={{ 
            padding: '0.75rem', 
            background: '#fee2e2', 
            color: '#991b1b', 
            borderRadius: '0.5rem', 
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '1.5rem',
          padding: '0.25rem',
          background: '#f3f4f6',
          borderRadius: '0.5rem'
        }}>
          <button
            onClick={() => setMode('code')}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: mode === 'code' ? 'white' : 'transparent',
              color: mode === 'code' ? '#9333ea' : '#6b7280',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: mode === 'code' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Hash style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
            Enter Code
          </button>
          <button
            onClick={() => setMode('search')}
            style={{
              flex: 1,
              padding: '0.75rem',
              border: 'none',
              borderRadius: '0.375rem',
              background: mode === 'search' ? 'white' : 'transparent',
              color: mode === 'search' ? '#9333ea' : '#6b7280',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.3s',
              boxShadow: mode === 'search' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none'
            }}
          >
            <Search style={{ width: '1rem', height: '1rem', display: 'inline', marginRight: '0.5rem' }} />
            Search Family
          </button>
        </div>

        {mode === 'code' ? (
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
              Family Code
            </label>
            <Input
              icon={Hash}
              type="text"
              placeholder="Enter 6-digit code"
              value={familyCode}
              onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
              maxLength={6}
              style={{ marginBottom: '1rem' }}
            />
            <Button 
              onClick={handleCodeSubmit}
              variant="primary" 
              style={{ width: '100%', justifyContent: 'center', marginBottom: '0.75rem' }}
              disabled={!familyCode || loading}
            >
              {loading ? 'Joining...' : 'Join Family'}
            </Button>
            <Button 
              onClick={handleSkip}
              variant="secondary" 
              style={{ width: '100%', justifyContent: 'center' }}
              disabled={loading}
            >
              Skip for Now
            </Button>
          </div>
        ) : (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <Input
                icon={Search}
                type="text"
                placeholder="Search by family name"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                style={{ flex: 1 }}
              />
              <Button onClick={handleSearch} variant="primary" disabled={loading}>
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>

            {selectedFamilies.length > 0 && (
              <div style={{ marginBottom: '1rem', padding: '0.75rem', background: '#f3e8ff', borderRadius: '0.5rem' }}>
                <p style={{ fontWeight: '600', color: '#7c3aed', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                  Selected Families ({selectedFamilies.length})
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {selectedFamilies.map(family => (
                    <span
                      key={family.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        padding: '0.25rem 0.5rem',
                        background: 'white',
                        borderRadius: '0.375rem',
                        fontSize: '0.75rem',
                        color: '#7c3aed'
                      }}
                    >
                      {family.name}
                      <button
                        onClick={() => toggleFamilySelection(family)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                      >
                        <X style={{ width: '0.875rem', height: '0.875rem' }} />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                  Select one or more families to join:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', maxHeight: '20rem', overflowY: 'auto' }}>
                  {searchResults.map(family => {
                    const isSelected = selectedFamilies.some(f => f.id === family.id);
                    return (
                      <div
                        key={family.id}
                        onClick={() => toggleFamilySelection(family)}
                        style={{
                          padding: '1rem',
                          border: isSelected ? '2px solid #9333ea' : '2px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          background: isSelected ? '#f3e8ff' : 'white',
                          cursor: 'pointer',
                          transition: 'all 0.3s'
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div>
                            <h3 style={{ fontWeight: '600', color: '#1f2937', marginBottom: '0.25rem' }}>
                              {family.name}
                            </h3>
                            <p style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                              {family.member_count} members
                            </p>
                          </div>
                          <div style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '50%',
                            border: isSelected ? 'none' : '2px solid #d1d5db',
                            background: isSelected ? '#9333ea' : 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {isSelected && <Check style={{ width: '1rem', height: '1rem', color: 'white' }} />}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {searchResults.length > 0 && selectedFamilies.length > 0 && (
                <Button 
                  onClick={handleSearchComplete}
                  variant="primary" 
                  style={{ width: '100%', justifyContent: 'center' }}
                  disabled={loading}
                >
                  {loading ? 'Joining...' : `Join ${selectedFamilies.length} ${selectedFamilies.length === 1 ? 'Family' : 'Families'}`}
                </Button>
              )}
              <Button 
                onClick={handleSkip}
                variant="secondary" 
                style={{ width: '100%', justifyContent: 'center' }}
                disabled={loading}
              >
                Skip for Now
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export const SignupPage = () => { 
  const navigate = useNavigate(); 
  const { showToast, ToastContainer } = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userType, setUserType] = useState('');
  const [showFamilyModal, setShowFamilyModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match!');
      return;
    }
    if (!userType) {
      setError('Please select your role!');
      return;
    }
    if (password.length < 5) {
      setError('Password must be at least 5 characters long');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        email: email.trim(),
        password: password,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        role: userType
      };

      const response = await AuthService.signup(userData);

      // Show success toast
      showToast('Account created successfully!', 'success');

      // Wait a bit to show the toast before redirecting
      setTimeout(() => {
        // If grandparent, show family selection modal
        if (userType === 'grandparent') {
          setShowFamilyModal(true);
          setLoading(false);
        } else {
          // Parent - navigate to dashboard
          navigate('/parent-dashboard');
        }
      }, 1500);
    } catch (err) {
      setError(err.message || 'Failed to create account');
      showToast(err.message || 'Failed to create account', 'error');
      setLoading(false);
    }
  };

  const handleFamilySelection = (families) => {
    setShowFamilyModal(false);
    navigate('/grandparent-dashboard');
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #faf5ff 0%, #fce7f3 50%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ width: '100%', maxWidth: '28rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
            <Book style={{ width: '2.5rem', height: '2.5rem', color: '#9333ea' }} />
            <span style={{ 
                fontSize: '2.5rem',
                fontWeight: '900',
                fontFamily: '"Comic Sans MS", "Chalkboard SE", "Comic Neue", cursive, sans-serif',
                display: 'flex', 
                gap: '0.1rem',
                letterSpacing: '0.05rem'
              }}>
                <span style={{ 
                  color: '#ff4757',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>R</span>
                <span style={{ 
                  color: '#ff6b35',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>e</span>
                <span style={{ 
                  color: '#ffd93d',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>a</span>
                <span style={{ 
                  color: '#a855f7',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>d</span>
                <span style={{ 
                  color: '#ffd93d',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>2</span>
                <span style={{ 
                  color: '#06b6d4',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>M</span>
                <span style={{ 
                  color: '#3b82f6',
                  WebkitTextStroke: '3px black',
                  paintOrder: 'stroke fill',
                  textShadow: '3px 3px 0px rgba(0,0,0,0.2)'
                }}>e</span>
              </span>
          </div>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#1f2937', marginBottom: '0.5rem' }}>Create Account</h1>
          <p style={{ color: '#6b7280', fontSize: '1rem' }}>Join and start sharing stories today</p>
        </div>

        <Card style={{ padding: '2rem' }}>
          {error && (
            <div style={{ 
              padding: '0.75rem', 
              background: '#fee2e2', 
              color: '#991b1b', 
              borderRadius: '0.5rem', 
              marginBottom: '1rem',
              fontSize: '0.875rem'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>I am a...</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => setUserType('grandparent')}
                  style={{
                    padding: '1rem',
                    border: userType === 'grandparent' ? '2px solid #9333ea' : '2px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    background: userType === 'grandparent' ? '#f3e8ff' : 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: userType === 'grandparent' ? '#9333ea' : '#4b5563',
                    transition: 'all 0.3s'
                  }}
                >
                  Grandparent
                </button>
                <button
                  type="button"
                  onClick={() => setUserType('parent')}
                  style={{
                    padding: '1rem',
                    border: userType === 'parent' ? '2px solid #db2777' : '2px solid #e5e7eb',
                    borderRadius: '0.75rem',
                    background: userType === 'parent' ? '#fce7f3' : 'white',
                    cursor: 'pointer',
                    fontWeight: '600',
                    color: userType === 'parent' ? '#db2777' : '#4b5563',
                    transition: 'all 0.3s'
                  }}
                >
                  Parent
                </button>
              </div>
              {userType === 'grandparent' && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  You can join families now or later from your dashboard
                </p>
              )}
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>First Name</label>
              <Input 
                icon={User}
                type="text" 
                placeholder="Rose" 
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Last Name</label>
              <Input 
                icon={User}
                type="text" 
                placeholder="Johnson" 
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Email</label>
              <Input 
                icon={Mail}
                type="email" 
                placeholder="rose@example.com" 
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Password</label>
              <Input 
                icon={Lock}
                type="password" 
                placeholder="••••••••" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Must be at least 5 characters
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>Confirm Password</label>
              <Input 
                icon={Lock}
                type="password" 
                placeholder="••••••••" 
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </div>

            <Button type="submit" variant="primary" style={{ width: '100%', justifyContent: 'center', padding: '1rem' }} disabled={loading}>
              {loading ? 'Creating Account...' : 'Create Account'}
              <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />
            </Button>
          </form>

          <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
            <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
              Already have an account?{' '}
              <button 
                onClick={() => navigate('/login')}
                style={{ color: '#9333ea', fontWeight: '600', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', textDecoration: 'underline' }}
              >
                Sign in
              </button>
            </p>
          </div>
        </Card>
      </div>

      {showFamilyModal && (
        <FamilySelectionModal
          onClose={() => {
            setShowFamilyModal(false);
            navigate('/grandparent-dashboard');
          }}
          onComplete={handleFamilySelection}
        />
      )}

      <ToastContainer />
    </div>
  );
};